import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { isAxiosError } from 'axios';

import { Section, SectionCard, SectionHeader } from '@/components/common';
import { AudienceFilterSelector } from '@/components/admin/desktop-notifications/AudienceFilterSelector';
import { ReminderBodyEditor } from '@/components/admin/desktop-notifications/ReminderBodyEditor';
import { ScheduleEditor } from '@/components/admin/desktop-notifications/ScheduleEditor';
import {
  useCreateDesktopNotification,
  useDeleteDesktopNotification,
  useDesktopNotification,
  useDesktopNotificationPlaceholders,
  useUpdateDesktopNotification,
} from '@/queries/useDesktopNotifications';
import { useAuthStore } from '@/stores/useAuthStore';
import { useNotificationStore } from '@/stores/useNotificationStore';
import { paths } from '@/routes/paths';
import {
  DESKTOP_NOTIFICATION_TYPES,
  DESKTOP_NOTIFICATION_TYPE_LABELS,
  type DesktopNotificationDetailDto,
  type DesktopNotificationType,
} from '@/api/types/desktopNotification.types';
import {
  validateNotificationForm,
  type NotificationReminderFormState,
} from './validateNotificationForm';

const DEFAULT_FORM: NotificationReminderFormState = {
  type: null,
  name: '',
  enabled: true,
  dismissEnabled: true,
  schedules: [
    {
      dayOfWeek: 'Friday',
      timeOfDay: '09:00:00',
      snoozeDurationMinutes: null,
      isGenerationAnchor: false,
    },
  ],
  timeZone: 'Europe/London',
  header: '',
  body: '',
  audience: {
    grades: [],
    geos: [],
    staffIds: [],
    excludedStaffIds: [],
    completionRateMinPercent: null,
    completionRateMaxPercent: null,
  },
};

function formFromDetail(d: DesktopNotificationDetailDto): NotificationReminderFormState {
  return {
    type: d.type,
    name: d.name,
    enabled: d.enabled,
    dismissEnabled: d.dismissEnabled,
    schedules: d.schedules.map((s) => ({ ...s })),
    timeZone: d.timeZone,
    header: d.header,
    body: d.body,
    audience: { ...d.audience },
  };
}

export function DesktopNotificationEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const permissions = useAuthStore((state) => state.permissions);
  const hasAnyRole = useAuthStore((state) => state.hasAnyRole);
  const canAccess = hasAnyRole(['Admin']);
  const showNotification = useNotificationStore((state) => state.showNotification);

  const numericId = Number(id);
  const isCreate = !id || numericId === 0;

  const detailQuery = useDesktopNotification(isCreate ? 0 : numericId);

  const createMutation = useCreateDesktopNotification();
  const updateMutation = useUpdateDesktopNotification();
  const deleteMutation = useDeleteDesktopNotification();

  const [form, setForm] = useState<NotificationReminderFormState | null>(
    isCreate ? { ...DEFAULT_FORM } : null,
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Scoped to the chosen type so the chips only offer tokens the server will accept for it. With no
  // type picked yet the call omits the parameter and every token comes back.
  const placeholdersQuery = useDesktopNotificationPlaceholders(form?.type);

  useEffect(() => {
    if (isCreate) return;
    if (!detailQuery.data) return;
    setForm(formFromDetail(detailQuery.data));
  }, [isCreate, detailQuery.data]);

  if (permissions && !canAccess) {
    return <Navigate to={paths.unauthorized} replace />;
  }

  const allowedPlaceholders = placeholdersQuery.data?.tokens ?? [];
  const allowedTokenNames = allowedPlaceholders.map((p) => p.token);

  const handleCancel = () => {
    navigate(paths.admin.desktopNotifications);
  };

  const handleChange = <K extends keyof NotificationReminderFormState>(
    key: K,
    value: NotificationReminderFormState[K],
  ) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setValidationError(null);
  };

  // Three things stop applying when the type changes, and each would otherwise sit in state invisibly:
  // completion rate (Happiness Survey only, and the server rejects the bounds on anything else), the
  // generation anchor (same - the server rejects the flag on the other three types), and named staff (the
  // survey-eligible option list is a strict subset, so crossing that boundary can leave ids behind that the
  // Autocomplete no longer has an option for - selected, unrenderable, still saved).
  // Switching between two non-survey types shares one option list, so selections survive that.
  const handleTypeChange = (event: SelectChangeEvent<DesktopNotificationType>) => {
    const type = event.target.value as DesktopNotificationType;
    setForm((prev) => {
      if (!prev) return prev;

      const isSurvey = type === 'HappinessSurvey';
      const audienceScopeChanged = isSurvey !== (prev.type === 'HappinessSurvey');

      return {
        ...prev,
        type,
        schedules: isSurvey
          ? prev.schedules
          : prev.schedules.map((s) => ({ ...s, isGenerationAnchor: false })),
        audience: {
          ...prev.audience,
          completionRateMinPercent: isSurvey ? prev.audience.completionRateMinPercent : null,
          completionRateMaxPercent: isSurvey ? prev.audience.completionRateMaxPercent : null,
          staffIds: audienceScopeChanged ? [] : prev.audience.staffIds,
          excludedStaffIds: audienceScopeChanged ? [] : prev.audience.excludedStaffIds,
        },
      };
    });
    setValidationError(null);
  };

  const handleScheduleChange = (next: Partial<NotificationReminderFormState>) => {
    setForm((prev) => (prev ? { ...prev, ...next } : prev));
    setValidationError(null);
  };

  const handleAudienceChange = (next: NotificationReminderFormState['audience']) => {
    setForm((prev) => (prev ? { ...prev, audience: next } : prev));
    setValidationError(null);
  };

  const handleHeaderBodyChange = ({ header, body }: { header: string; body: string }) => {
    setForm((prev) => (prev ? { ...prev, header, body } : prev));
    setValidationError(null);
  };

  const handleSave = async () => {
    if (!form) return;
    const err = validateNotificationForm(form, allowedTokenNames);
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError(null);

    try {
      if (isCreate) {
        if (!form.type) return;
        await createMutation.mutateAsync({
          type: form.type,
          name: form.name,
          enabled: form.enabled,
          dismissEnabled: form.dismissEnabled,
          schedules: form.schedules,
          timeZone: form.timeZone,
          header: form.header,
          body: form.body,
          audience: form.audience,
        });
        showNotification('Reminder created.', 'success');
        navigate(paths.admin.desktopNotifications);
      } else {
        if (!detailQuery.data) return;
        await updateMutation.mutateAsync({
          id: numericId,
          request: {
            name: form.name,
            enabled: form.enabled,
            dismissEnabled: form.dismissEnabled,
            schedules: form.schedules,
            timeZone: form.timeZone,
            header: form.header,
            body: form.body,
            audience: form.audience,
            rowVersion: detailQuery.data.rowVersion,
          },
        });
        showNotification('Reminder saved.', 'success');
        navigate(paths.admin.desktopNotifications);
      }
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        if (!isCreate && detailQuery.data) {
          setForm(formFromDetail(detailQuery.data));
        }
        return;
      }
      showNotification('Failed to save reminder.', 'error');
    }
  };

  const handleDelete = async () => {
    if (isCreate || !detailQuery.data) return;
    try {
      await deleteMutation.mutateAsync({
        id: numericId,
        rowVersion: detailQuery.data.rowVersion,
      });
      showNotification('Reminder deleted.', 'success');
      navigate(paths.admin.desktopNotifications);
    } catch {
      showNotification('Failed to delete reminder.', 'error');
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  if (!isCreate && detailQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isCreate && (detailQuery.isError || !detailQuery.data)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" role="alert">
          Failed to load reminder.
        </Alert>
      </Box>
    );
  }

  if (!form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const title = isCreate ? 'Create reminder' : `Edit reminder: ${detailQuery.data?.name ?? ''}`;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <IconButton
          onClick={() => navigate(paths.admin.desktopNotifications)}
          aria-label="Back to desktop notifications"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {title}
        </Typography>
      </Box>

      {validationError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }} data-testid="form-validation-error">
          {validationError}
        </Alert>
      )}

      <Section>
        <SectionHeader>BASICS</SectionHeader>
        <SectionCard>
          <Stack spacing={2}>
            {isCreate ? (
              <FormControl size="small" required sx={{ maxWidth: 280 }}>
                <InputLabel id="desktop-notification-type-label">Type</InputLabel>
                <Select
                  labelId="desktop-notification-type-label"
                  label="Type"
                  value={form.type ?? ''}
                  onChange={handleTypeChange}
                  inputProps={{ 'aria-label': 'Type', 'aria-required': true }}
                >
                  {DESKTOP_NOTIFICATION_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {DESKTOP_NOTIFICATION_TYPE_LABELS[t]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  Type (cannot be changed after creation)
                </Typography>
                <Chip
                  label={form.type ? DESKTOP_NOTIFICATION_TYPE_LABELS[form.type] : ''}
                  size="small"
                  variant="outlined"
                  color="secondary"
                  aria-label="Notification type"
                />
              </Box>
            )}
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              inputProps={{ maxLength: 200, 'aria-label': 'Name' }}
              required
            />
            {/* Both switches sit in a Box so they line up: a bare FormControlLabel is a flex item of
                the Stack and blockifies, while one inside a Box stays inline-flex, and the two take
                its -11px margin differently. */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.enabled}
                    onChange={(e) => handleChange('enabled', e.target.checked)}
                    slotProps={{ input: { 'aria-label': 'Enabled' } }}
                  />
                }
                label="Enabled"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.dismissEnabled}
                    onChange={(e) => handleChange('dismissEnabled', e.target.checked)}
                    slotProps={{
                      input: {
                        'aria-label': 'Dismiss enabled',
                        'aria-describedby': 'dismiss-enabled-help',
                      },
                    }}
                  />
                }
                label="Dismiss enabled"
              />
              <Typography
                id="dismiss-enabled-help"
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                When off, the pop-up shows no Dismiss button.
              </Typography>
            </Box>
          </Stack>
        </SectionCard>
      </Section>

      <Section>
        <SectionHeader>SCHEDULE</SectionHeader>
        <SectionCard>
          <ScheduleEditor
            schedules={form.schedules}
            timeZone={form.timeZone}
            type={form.type}
            onChange={handleScheduleChange}
          />
        </SectionCard>
      </Section>

      <Section>
        <SectionHeader>AUDIENCE</SectionHeader>
        <SectionCard>
          <AudienceFilterSelector
            value={form.audience}
            type={form.type}
            onChange={handleAudienceChange}
          />
        </SectionCard>
      </Section>

      <Section>
        <SectionHeader>MESSAGE</SectionHeader>
        <SectionCard>
          <ReminderBodyEditor
            header={form.header}
            body={form.body}
            allowedTokens={allowedPlaceholders}
            onChange={handleHeaderBodyChange}
          />
        </SectionCard>
      </Section>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        {!isCreate && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteConfirmOpen(true)}
            sx={{ mr: 'auto' }}
          >
            Delete
          </Button>
        )}
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="success" onClick={handleSave} disabled={isSaving}>
          Save
        </Button>
      </Box>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-reminder-edit-title"
      >
        <DialogTitle id="delete-reminder-edit-title">Delete reminder?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete &quot;{detailQuery.data?.name}&quot;? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DesktopNotificationEditPage;
